import { create } from "zustand";

import {
  readThemePreference,
  readThemePreferenceSync,
  writeThemePreference,
  type StoredThemePreference,
} from "@/lib/theme-preference-io";

export type ThemePreference = StoredThemePreference;

/**
 * The appearance choice, held once for the whole app.
 *
 * ## Why it is a store, and not provider state
 *
 * `useResolvedAppColors` reads this from ABOVE `AppThemeProvider` — the root
 * view has to be painted before the provider exists — and the two must never
 * disagree. Provider state cannot be read from outside the provider, and a
 * second independent read would stop repainting the root view the moment
 * somebody changed the theme at runtime: the app would go dark around a
 * background that stayed light until reload.
 *
 * ## Why it is its OWN MODULE, and not a `const` inside `app-theme.tsx`
 *
 * Because there are two theme resolvers in this app, not one, and the second
 * one silently stopped working.
 *
 * `useTheme()` in `hooks/use-theme.ts` serves 58 files — every screen pushed on
 * top of a tab: Invite, Language, Upgrade, Support, Dashboard. `useAppTheme()`
 * and `useResolvedAppColors()` serve the tabs. When the preference moved off
 * the auth store, `app-theme.tsx` was updated and `use-theme.ts` was not: it
 * went on reading `themePreference` off the auth store through a cast, found a
 * field that no longer existed, and resolved `device` forever. So the tabs
 * honoured the choice and everything you could navigate to from them ignored
 * it — which is exactly what a light-theme user saw.
 *
 * Keeping the store private to one of the two resolvers is what made that
 * possible. In its own module neither owns it, both import it, and there is no
 * version of "updated one of them" that leaves the other reading somewhere
 * else. `tests/mobile/theme-preference.test.ts` scans for any other reader.
 *
 * ## Seeding
 *
 * Synchronous, so the FIRST render already holds the saved choice on web. Only
 * web can answer synchronously; on native this is `device` until `hydrate`
 * lands, which is a frame on a surface that is not reloaded on every visit.
 */
export const useThemePreferenceStore = create<{
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
  hydrate: () => Promise<void>;
}>((set, get) => ({
  preference: readThemePreferenceSync() ?? "device",

  setPreference: (next) => {
    // State first, disk second. The write is a promise and the repaint must
    // not wait on it: a failed write costs the setting on the NEXT launch,
    // where a delayed repaint costs a visibly sticky control on this one.
    set({ preference: next });
    void writeThemePreference(next);
  },

  hydrate: async () => {
    const saved = await readThemePreference();
    // Only if nothing has been chosen since. A tap that happened while this
    // read was in flight is newer than the disk, and must not be undone by it.
    if (get().preference === "device") {
      set({ preference: saved });
    }
  },
}));

/**
 * The saved choice. The ONE source every resolver reads.
 *
 * Both `useAppTheme` / `useResolvedAppColors` and `hooks/use-theme` call this.
 * Nothing else should resolve a theme, and nothing at all should read a theme
 * preference off the auth store — a theme belongs to a DEVICE, not an account,
 * and keeping it out of that store is what stops it being entangled with
 * sign-out.
 */
export function useThemePreference(): ThemePreference {
  return useThemePreferenceStore((state) => state.preference);
}
